import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getDenialApiBaseUrl } from '../utils/env';
import './partner-registration.css';

const API = getDenialApiBaseUrl();

const ORG_TYPES = [
  'Hospital', 'Health System', 'Physician Group',
  'Billing Company', 'RCM Company', 'Other',
];

const EMPLOYEE_COUNTS = [
  '1–50', '51–200', '201–500', '501–1,000', '1,001–5,000', '5,000+',
];

const EHR_SYSTEMS = [
  'Epic', 'Cerner / Oracle Health', 'Athena', 'eClinicalWorks',
  'Meditech', 'NextGen', 'Other',
];

const PAYER_OPTIONS = [
  'Commercial', 'Medicare', 'Medicaid', 'Workers Comp', 'VA / Tricare', 'Other',
];

const TIMELINES = [
  'ASAP', '1–3 months', '3–6 months', '6+ months',
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
  'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
  'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
  'District of Columbia', 'Puerto Rico', 'Multi-state', 'National',
];

function countWords(text) {
  if (!text) return 0;
  return text.trim().replace(/\s+/g, ' ').split(' ').filter(Boolean).length;
}

const INITIAL = {
  first_name: '', last_name: '', job_title: '', work_email: '', phone: '',
  company_name: '', org_type: '', employee_count: '', state_region: '', ehr_system: '',
  monthly_claim_volume: '', monthly_denial_volume: '',
  payer_mix: [],
  top_denial_reasons: '', current_tools: '',
  interest_reason: '', ideal_outcome: '', start_timeline: '',
  consent_given: false,
  website_url_confirm: '',
};

export default function PartnerRegistrationPage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Design Partnership — RCMBoost by CerebralMachines.ai';
    window.scrollTo(0, 0);
  }, []);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: '' }));
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    set(name, type === 'checkbox' ? checked : value);
  };

  const onBlur = (e) => {
    setTouched((t) => ({ ...t, [e.target.name]: true }));
  };

  const togglePayer = (val) => {
    setForm((f) => {
      const mix = f.payer_mix.includes(val)
        ? f.payer_mix.filter((v) => v !== val)
        : [...f.payer_mix, val];
      return { ...f, payer_mix: mix };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'First name is required';
    if (!form.last_name.trim()) e.last_name = 'Last name is required';
    if (!form.job_title.trim()) e.job_title = 'Title/role is required';
    if (!form.work_email.trim()) e.work_email = 'Work email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email))
      e.work_email = 'Please enter a valid email address';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    else if (form.phone.replace(/[^\d]/g, '').length < 10)
      e.phone = 'Please enter a valid phone number';
    if (!form.company_name.trim()) e.company_name = 'Company name is required';
    if (!form.org_type) e.org_type = 'Organization type is required';
    if (!form.employee_count) e.employee_count = 'Employee count is required';
    if (!form.state_region) e.state_region = 'State/region is required';
    if (!form.ehr_system) e.ehr_system = 'EHR/PMS system is required';
    if (!form.interest_reason.trim()) e.interest_reason = 'This field is required';
    else if (countWords(form.interest_reason) > 400)
      e.interest_reason = 'Please keep to 400 words or fewer';
    if (!form.ideal_outcome.trim()) e.ideal_outcome = 'This field is required';
    else if (countWords(form.ideal_outcome) > 400)
      e.ideal_outcome = 'Please keep to 400 words or fewer';
    if (!form.start_timeline) e.start_timeline = 'Timeline is required';
    if (!form.consent_given) e.consent_given = 'You must agree to be contacted';
    if (form.top_denial_reasons && countWords(form.top_denial_reasons) > 300)
      e.top_denial_reasons = 'Please keep to 300 words or fewer';
    if (form.current_tools && countWords(form.current_tools) > 200)
      e.current_tools = 'Please keep to 200 words or fewer';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setSubmitError('');
    const allTouched = {};
    Object.keys(form).forEach((k) => { allTouched[k] = true; });
    setTouched(allTouched);
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        monthly_claim_volume: parseInt(form.monthly_claim_volume, 10) || 0,
        monthly_denial_volume: parseInt(form.monthly_denial_volume, 10) || 0,
        utm_source: searchParams.get('utm_source') || undefined,
        utm_medium: searchParams.get('utm_medium') || undefined,
        utm_campaign: searchParams.get('utm_campaign') || undefined,
        utm_term: searchParams.get('utm_term') || undefined,
        utm_content: searchParams.get('utm_content') || undefined,
      };
      await axios.post(`${API}/partner-registrations`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Something went wrong. Please try again.';
      setSubmitError(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="partner-page">
        <Header />
        <section className="partner-success">
          <div className="partner-success__card">
            <div className="partner-success__icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h1 className="partner-success__title">Application Received</h1>
            <p className="partner-success__body">
              Thank you for your interest in the Design Partnership program. Our team will
              review your application and reach out within a few business days to discuss
              next steps.
            </p>
            <Link to="/" className="partner-btn--back">Back to Home</Link>
          </div>
        </section>
      </div>
    );
  }

  const err = (name) => touched[name] && errors[name];

  return (
    <div className="partner-page">
      <Header />

      <section className="partner-hero">
        <div className="partner__wrap partner-hero__inner">
          <span className="partner-hero__eyebrow"><span className="dot" /> Design Partnership Program</span>
          <h1 className="partner-hero__title">
            Be part of the $40B<br /><span className="grad">denial recovery opportunity.</span>
          </h1>
          <p className="partner-hero__subtitle">
            Join a select group of healthcare organizations partnering with us to
            eliminate low-value denials and unlock revenue that's being left on the table.
          </p>
        </div>
      </section>

      <section className="partner-form-section">
        <div className="partner__wrap partner__wrap--narrow">
          {submitError && (
            <div className="partner-error-banner">{submitError}</div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Honeypot */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
              <input tabIndex={-1} autoComplete="off" name="website_url_confirm"
                value={form.website_url_confirm} onChange={onChange} />
            </div>

            {/* ── Contact Information ── */}
            <div className="partner-card">
              <h2 className="partner-card__title">Contact Information</h2>
              <div className="partner-grid partner-grid--2">
                <Field label="First Name" required error={err('first_name')}>
                  <input className={`partner-input ${err('first_name') ? 'partner-input--error' : ''}`}
                    name="first_name" value={form.first_name} onChange={onChange} onBlur={onBlur}
                    placeholder="First name" maxLength={80} />
                </Field>
                <Field label="Last Name" required error={err('last_name')}>
                  <input className={`partner-input ${err('last_name') ? 'partner-input--error' : ''}`}
                    name="last_name" value={form.last_name} onChange={onChange} onBlur={onBlur}
                    placeholder="Last name" maxLength={80} />
                </Field>
              </div>
              <div className="partner-grid partner-grid--2">
                <Field label="Title / Role" required error={err('job_title')}>
                  <input className={`partner-input ${err('job_title') ? 'partner-input--error' : ''}`}
                    name="job_title" value={form.job_title} onChange={onChange} onBlur={onBlur}
                    placeholder="e.g. VP of Revenue Cycle" maxLength={150} />
                </Field>
                <Field label="Phone Number" required error={err('phone')}>
                  <input className={`partner-input ${err('phone') ? 'partner-input--error' : ''}`}
                    name="phone" type="tel" value={form.phone} onChange={onChange} onBlur={onBlur}
                    placeholder="(555) 555-5555" maxLength={30} />
                </Field>
              </div>
              <div className="partner-grid">
                <Field label="Work Email" required error={err('work_email')}>
                  <input className={`partner-input ${err('work_email') ? 'partner-input--error' : ''}`}
                    name="work_email" type="email" value={form.work_email} onChange={onChange} onBlur={onBlur}
                    placeholder="name@company.com" maxLength={255} />
                </Field>
              </div>
            </div>

            {/* ── Organization ── */}
            <div className="partner-card">
              <h2 className="partner-card__title">Organization</h2>
              <div className="partner-grid partner-grid--2">
                <Field label="Company Name" required error={err('company_name')}>
                  <input className={`partner-input ${err('company_name') ? 'partner-input--error' : ''}`}
                    name="company_name" value={form.company_name} onChange={onChange} onBlur={onBlur}
                    placeholder="Company name" maxLength={200} />
                </Field>
                <Field label="Organization Type" required error={err('org_type')}>
                  <select className={`partner-select ${err('org_type') ? 'partner-select--error' : ''}`}
                    name="org_type" value={form.org_type} onChange={onChange} onBlur={onBlur}>
                    <option value="">Select type</option>
                    {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <div className="partner-grid partner-grid--2">
                <Field label="Number of Employees" required error={err('employee_count')}>
                  <select className={`partner-select ${err('employee_count') ? 'partner-select--error' : ''}`}
                    name="employee_count" value={form.employee_count} onChange={onChange} onBlur={onBlur}>
                    <option value="">Select range</option>
                    {EMPLOYEE_COUNTS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="State / Region" required error={err('state_region')}>
                  <select className={`partner-select ${err('state_region') ? 'partner-select--error' : ''}`}
                    name="state_region" value={form.state_region} onChange={onChange} onBlur={onBlur}>
                    <option value="">Select state</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <div className="partner-grid">
                <Field label="EHR / PMS System" required error={err('ehr_system')}>
                  <select className={`partner-select ${err('ehr_system') ? 'partner-select--error' : ''}`}
                    name="ehr_system" value={form.ehr_system} onChange={onChange} onBlur={onBlur}>
                    <option value="">Select system</option>
                    {EHR_SYSTEMS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {/* ── Denial Operations ── */}
            <div className="partner-card">
              <h2 className="partner-card__title">Denial Operations</h2>
              <div className="partner-grid partner-grid--2">
                <Field label="Monthly Claim Volume">
                  <input className="partner-input" name="monthly_claim_volume" type="number" min="0"
                    value={form.monthly_claim_volume} onChange={onChange}
                    placeholder="e.g. 5000" />
                </Field>
                <Field label="Monthly Denial Volume">
                  <input className="partner-input" name="monthly_denial_volume" type="number" min="0"
                    value={form.monthly_denial_volume} onChange={onChange}
                    placeholder="e.g. 800" />
                </Field>
              </div>

              <div className="partner-grid">
                <Field label="Primary Payer Mix">
                  <div className="partner-checkbox-group">
                    {PAYER_OPTIONS.map((opt) => (
                      <label key={opt} className="partner-checkbox-label">
                        <input type="checkbox" checked={form.payer_mix.includes(opt)}
                          onChange={() => togglePayer(opt)} />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </Field>
              </div>

              <div className="partner-grid">
                <Field label="Top Denial Reason Categories" error={err('top_denial_reasons')}
                  hint="What types of denials cause the most rework or write-offs?">
                  <textarea className={`partner-textarea ${err('top_denial_reasons') ? 'partner-textarea--error' : ''}`}
                    name="top_denial_reasons" rows={4} value={form.top_denial_reasons}
                    onChange={onChange} onBlur={onBlur}
                    placeholder="e.g. Medical necessity, eligibility, prior auth, coding errors..." />
                  <span className={`partner-word-count ${countWords(form.top_denial_reasons) > 300 ? 'partner-word-count--over' : ''}`}>
                    {countWords(form.top_denial_reasons)} / 300 words
                  </span>
                </Field>
              </div>

              <div className="partner-grid">
                <Field label="Current Denial Management Tools / Process" error={err('current_tools')}
                  hint="What tools or workflow do you use today to handle denials?">
                  <textarea className={`partner-textarea ${err('current_tools') ? 'partner-textarea--error' : ''}`}
                    name="current_tools" rows={3} value={form.current_tools}
                    onChange={onChange} onBlur={onBlur}
                    placeholder="e.g. Manual spreadsheet tracking, Waystar, Change Healthcare..." />
                  <span className={`partner-word-count ${countWords(form.current_tools) > 200 ? 'partner-word-count--over' : ''}`}>
                    {countWords(form.current_tools)} / 200 words
                  </span>
                </Field>
              </div>
            </div>

            {/* ── Partnership Interest ── */}
            <div className="partner-card">
              <h2 className="partner-card__title">Partnership Interest</h2>

              <div className="partner-grid">
                <Field label="Why are you interested in this program?" required error={err('interest_reason')}>
                  <textarea className={`partner-textarea ${err('interest_reason') ? 'partner-textarea--error' : ''}`}
                    name="interest_reason" rows={5} value={form.interest_reason}
                    onChange={onChange} onBlur={onBlur}
                    placeholder="What drew you to the Design Partnership program?" />
                  <span className={`partner-word-count ${countWords(form.interest_reason) > 400 ? 'partner-word-count--over' : ''}`}>
                    {countWords(form.interest_reason)} / 400 words
                  </span>
                </Field>
              </div>

              <div className="partner-grid">
                <Field label="Your ideal outcome from this program" required error={err('ideal_outcome')}>
                  <textarea className={`partner-textarea ${err('ideal_outcome') ? 'partner-textarea--error' : ''}`}
                    name="ideal_outcome" rows={5} value={form.ideal_outcome}
                    onChange={onChange} onBlur={onBlur}
                    placeholder="What would success look like for your organization?" />
                  <span className={`partner-word-count ${countWords(form.ideal_outcome) > 400 ? 'partner-word-count--over' : ''}`}>
                    {countWords(form.ideal_outcome)} / 400 words
                  </span>
                </Field>
              </div>

              <div className="partner-grid">
                <Field label="Desired Start Timeline" required error={err('start_timeline')}>
                  <select className={`partner-select ${err('start_timeline') ? 'partner-select--error' : ''}`}
                    name="start_timeline" value={form.start_timeline} onChange={onChange} onBlur={onBlur}>
                    <option value="">Select timeline</option>
                    {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {/* ── Consent & Submit ── */}
            <div className="partner-card">
              <div className="partner-grid">
                <div className="partner-field">
                  <label className={`partner-consent ${err('consent_given') ? 'partner-consent--error' : ''}`}>
                    <input type="checkbox" name="consent_given"
                      checked={form.consent_given} onChange={onChange} />
                    <span>
                      I agree to be contacted by CerebralMachines regarding the Design
                      Partnership program.
                    </span>
                  </label>
                  {err('consent_given') && <span className="partner-field-error">{errors.consent_given}</span>}
                </div>
              </div>
              <button type="submit" className="partner-btn--submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Apply for Design Partnership'}
              </button>
            </div>
          </form>
        </div>
      </section>

      <footer className="partner-footer">
        <p>&copy; {new Date().getFullYear()} CerebralMachines, Inc. All rights reserved.</p>
      </footer>
    </div>
  );
}

function Header() {
  return (
    <header className="partner-header">
      <div className="partner__wrap partner-header__inner">
        <Link to="/" className="partner-header__brand">
          <img
            src={`${process.env.PUBLIC_URL || ''}/rcmboost-logo-dark.png`}
            alt="RCMBoost — by CerebralMachines.ai"
          />
        </Link>
        <div className="partner-header__actions">
          <Link to="/login" className="partner-header__link">Sign In</Link>
        </div>
      </div>
    </header>
  );
}

function Field({ label, required, error, hint, children }) {
  return (
    <div className="partner-field">
      <label className="partner-label">
        {label} {required && <span className="partner-req">*</span>}
      </label>
      {children}
      {hint && <span className="partner-hint">{hint}</span>}
      {error && <span className="partner-field-error">{error}</span>}
    </div>
  );
}
